/**
 * Tiered Reveal System - Marketing Strategy for Capacity Display
 *
 * Creates urgency through strategic messaging that adapts to availability.
 * Based on "Capacity Card Marketing Strategy: 3 Approaches to Create Urgency"
 *
 * Approach 1: Tiered Reveal System
 * - Hides exact numbers when plenty available (> 50%)
 * - Shows social proof in mid-range (25-50%)
 * - Displays countdown urgency when low (< 25%)
 */

import { CalculatedEventStatus } from './eventStatus'

export interface CapacityStatus {
  tier: 'abundant' | 'selling-fast' | 'low' | 'critical' | 'sold-out' | 'event-live' | 'event-concluded'
  icon: string
  heading: string
  message: string
  bgColor: string
  textColor: string
  showNumbers: boolean
  showProgressBar: boolean
  showReservedCount: boolean
}

export const getCapacityStatus = (
  percentUsed: number,
  currentCount: number,
  remainingCount: number,
  eventStatus?: CalculatedEventStatus
): CapacityStatus => {
  // Event Concluded: Event is over
  if (eventStatus === 'Completed') {
    return {
      tier: 'event-concluded',
      icon: '✅',
      heading: 'Event Concluded',
      message: 'We hope you enjoyed your tails!',
      bgColor: 'bg-[#4A0E1F]/10 dark:bg-[#4A0E1F]/20',
      textColor: 'text-gray-800 dark:text-gray-300',
      showNumbers: false,
      showProgressBar: false,
      showReservedCount: false
    }
  }

  // Event in Progress: Pre-orders are closed
  if (eventStatus === 'In Progress' || eventStatus === 'Enrollment Closed') {
    return {
      tier: 'event-live',
      icon: '🦞',
      heading: 'Event Live',
      message: 'Pre-orders are now closed.',
      bgColor: 'bg-gray-500/10 dark:bg-gray-500/20',
      textColor: 'text-gray-800 dark:text-gray-300',
      showNumbers: false,
      showProgressBar: false,
      showReservedCount: false
    }
  }
  // Sold Out: No capacity remaining
  if (remainingCount <= 0) {
    return {
      tier: 'sold-out',
      icon: '🔒',
      heading: 'Sold Out',
      message: 'This event has reached capacity',
      bgColor: 'bg-gray-100 dark:bg-gray-900/20',
      textColor: 'text-gray-800 dark:text-gray-200',
      showNumbers: false,
      showProgressBar: false,
      showReservedCount: false
    }
  }

  // Tier 4: Critical (< 15% remaining = > 85% used)
  if (percentUsed > 85) {
    return {
      tier: 'critical',
      icon: '🚨',
      heading: `Final ${remainingCount} Tails!`,
      message: 'Order now before it\'s too late',
      bgColor: 'bg-red-100 dark:bg-red-900/20',
      textColor: 'text-red-800 dark:text-red-200',
      showNumbers: true,
      showProgressBar: true,
      showReservedCount: false
    }
  }

  // Tier 3: Low (15-25% remaining = 75-85% used)
  if (percentUsed > 75) {
    return {
      tier: 'low',
      icon: '⚠️',
      heading: 'Almost Sold Out!',
      message: `Only ${remainingCount} tails left`,
      bgColor: 'bg-orange-100 dark:bg-orange-900/20',
      textColor: 'text-orange-800 dark:text-orange-200',
      showNumbers: true,
      showProgressBar: true,
      showReservedCount: false
    }
  }

  // Tier 2: Selling Fast (25-50% remaining = 50-75% used)
  if (percentUsed > 50) {
    return {
      tier: 'selling-fast',
      icon: '⚡',
      heading: 'Selling Fast!',
      message: `Join ${currentCount} members who've already ordered`,
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/20',
      textColor: 'text-yellow-800 dark:text-yellow-200',
      showNumbers: false,
      showProgressBar: false,
      showReservedCount: true
    }
  }

  // Tier 1: Abundant (> 50% remaining = < 50% used)
  // Only show during enrollment period (Active/Scheduled)
  return {
    tier: 'abundant',
    icon: '🎟️',
    heading: 'Pre-Orders Open',
    message: 'Secure your tails before they sell out!',
    bgColor: 'bg-[#8B1538]/10 dark:bg-[#8B1538]/20',
    textColor: 'text-[#8B1538] dark:text-[#d4577a]',
    showNumbers: false,
    showProgressBar: false,
    showReservedCount: false
  }
}
